import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/data-display/tooltip";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { useState } from "react";

interface RationaleTooltipProps {
  /**
   * The rationale text to display in the tooltip
   */
  rationale: string | null;
  /**
   * The title/question to display in the tooltip header
   */
  title: string;
  /**
   * Icon to display next to the title
   */
  icon?: React.ReactNode;
  /**
   * The trigger element (what the user hovers/clicks on)
   */
  children: React.ReactNode;
  /**
   * Whether the tooltip should be open (controlled)
   */
  open?: boolean;
  /**
   * Callback when tooltip open state changes
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Alignment of the tooltip content
   */
  align?: "start" | "center" | "end";
  /**
   * Maximum width of the tooltip content
   */
  maxWidth?: string;
}

/**
 * Tooltip content component that displays rationale text.
 * Can be used independently with a custom trigger.
 */
export const RationaleTooltipContent: React.FC<{
  rationale: string | null;
  title: string;
  icon?: React.ReactNode;
  maxWidth?: string;
}> = ({ rationale, title, icon, maxWidth = "360px" }) => {
  if (!rationale) {
    return null;
  }

  return (
    <div className="flex flex-row space-x-1 items-start text-slate-700/90 mb-1">
      {icon && <div className="shrink-0">{icon}</div>}
      <div style={{ width: maxWidth }} className="text-xs">
        {typeof title === "string" ? (
          <h6 className="font-bold whitespace-nowrap leading-4 mb-0.5">
            {title}
          </h6>
        ) : (
          title
        )}
        <p className="font-normal text-xs whitespace-normal wrap-break-word">
          {rationale}
        </p>
      </div>
    </div>
  );
};

/**
 * Rationale tooltip component that displays sentiment rationale or other rationale text.
 * Supports both controlled and uncontrolled modes.
 */
const RationaleTooltip: React.FC<RationaleTooltipProps> = ({
  rationale,
  title,
  icon,
  children,
  open: controlledOpen,
  onOpenChange,
  align = "end",
  maxWidth = "360px",
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;

  const handleOpenChange = (newOpen: boolean) => {
    if (!isControlled) {
      setInternalOpen(newOpen);
    }
    onOpenChange?.(newOpen);
  };

  const handleTooltipTriggerClick = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    handleOpenChange(true);
  };

  // Don't render tooltip if there's no rationale
  if (!rationale) {
    return <>{children}</>;
  }

  return (
    <TooltipProvider>
      <Tooltip open={open} onOpenChange={handleOpenChange}>
        <TooltipTrigger asChild>
          <button
            className="flex flex-row space-x-1 items-center"
            onClick={handleTooltipTriggerClick}
            type="button"
          >
            {children}
          </button>
        </TooltipTrigger>
        <TooltipPrimitive.Portal>
          <TooltipContent align={align} className="px-2 py-4 max-w-md">
            <RationaleTooltipContent
              rationale={rationale}
              title={title}
              icon={icon}
              maxWidth={maxWidth}
            />
          </TooltipContent>
        </TooltipPrimitive.Portal>
      </Tooltip>
    </TooltipProvider>
  );
};

export default RationaleTooltip;
